// Generative ambient drone + gesture sounds. Per-feeling palettes; ducks under the voice.
export const DRONE_PALETTES = {
  title: { root: 55, ratios: [1, 1.5, 2, 3.01, 4], wave: 'sine', cutoff: 520, noise: 0.025, noiseHz: 900, trem: [0.1, 0.05], scale: [0, 3, 7, 10], plink: 'sine', level: 0.14 },
  curiosity: { root: 65.4, ratios: [1, 1.5, 2.25, 3, 4.5], wave: 'triangle', cutoff: 1100, noise: 0.06, noiseHz: 3200, trem: [0.2, 0.1], scale: [0, 2, 7, 9, 14], plink: 'sine', level: 0.17 },
  fear: { root: 49, ratios: [1, 1.06, 1.41, 2.12, 2.83], wave: 'sawtooth', cutoff: 700, noise: 0.08, noiseHz: 600, trem: [7, 0.45], scale: [0, 1, 6, 7], plink: 'square', level: 0.14 },
  trust: { root: 58.3, ratios: [1, 1.5, 2, 2.5, 3], wave: 'triangle', cutoff: 900, noise: 0.025, noiseHz: 1200, trem: [0.15, 0.06], scale: [0, 4, 7, 11], plink: 'sine', level: 0.19 },
  joy: { root: 73.4, ratios: [1, 1.25, 1.5, 2, 3], wave: 'triangle', cutoff: 1900, noise: 0.035, noiseHz: 4000, trem: [3, 0.15], scale: [0, 4, 7, 12, 16, 19], plink: 'triangle', level: 0.16 },
  grief: { root: 43.65, ratios: [1, 1.2, 1.5, 2.4, 3.6], wave: 'sine', cutoff: 420, noise: 0.05, noiseHz: 500, trem: [0.07, 0.1], scale: [0, 3, 7, 8], plink: 'sine', level: 0.2 },
  unity: { root: 55, ratios: [1, 1.5, 2, 2.5, 3], wave: 'sawtooth', cutoff: 1400, noise: 0.04, noiseHz: 1800, trem: [0.25, 0.08], scale: [0, 4, 7, 12, 14, 19], plink: 'sine', level: 0.21 },
};

export function createDrone(ctx, out) {
  let pal = DRONE_PALETTES.title;
  const bus = ctx.createGain();
  bus.gain.value = 0;
  const duck = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.Q.value = 3;
  filter.frequency.value = pal.cutoff;
  const trem = ctx.createGain();
  const tremLfo = ctx.createOscillator();
  const tremDepth = ctx.createGain();
  tremLfo.connect(tremDepth).connect(trem.gain);
  filter.connect(trem).connect(bus).connect(duck).connect(out);

  const oscs = pal.ratios.map((ratio) => {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = pal.wave;
    o.frequency.value = pal.root * ratio;
    o.detune.value = (Math.random() - 0.5) * 10;
    g.gain.value = 0.22;
    o.connect(g).connect(filter);
    o.start();
    return { o, g };
  });

  const noiseBuf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
  const nd = noiseBuf.getChannelData(0);
  for (let i = 0; i < nd.length; i += 1) nd[i] = Math.random() * 2 - 1;
  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuf;
  noise.loop = true;
  const noiseBp = ctx.createBiquadFilter();
  noiseBp.type = 'bandpass';
  noiseBp.Q.value = 0.7;
  const noiseGain = ctx.createGain();
  noise.connect(noiseBp).connect(noiseGain).connect(bus);
  noise.start();
  tremLfo.start();

  const fx = ctx.createGain();
  fx.gain.value = 0.9;
  const echo = ctx.createDelay(1);
  echo.delayTime.value = 0.33;
  const fb = ctx.createGain();
  fb.gain.value = 0.42;
  fx.connect(out);
  fx.connect(echo).connect(fb).connect(echo);
  echo.connect(out);

  const T = () => ctx.currentTime;
  function setFeeling(name) {
    pal = DRONE_PALETTES[name] || DRONE_PALETTES.title;
    oscs.forEach(({ o }, i) => {
      o.type = pal.wave;
      o.frequency.setTargetAtTime(pal.root * pal.ratios[i], T(), 1.2);
    });
    noiseBp.frequency.setTargetAtTime(pal.noiseHz, T(), 1);
    noiseGain.gain.setTargetAtTime(pal.noise, T(), 1);
    tremLfo.frequency.setTargetAtTime(pal.trem[0], T(), 0.5);
    tremDepth.gain.setTargetAtTime(pal.trem[1], T(), 0.5);
    trem.gain.setTargetAtTime(1 - pal.trem[1], T(), 0.5);
  }
  setFeeling('title');

  function tone({ freq, wave = 'sine', gain = 0.12, attack = 0.005, decay = 0.6, dest = fx, endFreq }) {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = wave;
    o.frequency.setValueAtTime(freq, T());
    if (endFreq) o.frequency.exponentialRampToValueAtTime(endFreq, T() + decay);
    g.gain.setValueAtTime(0.0001, T());
    g.gain.exponentialRampToValueAtTime(gain, T() + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, T() + attack + decay);
    o.connect(g).connect(dest);
    o.start();
    o.stop(T() + attack + decay + 0.05);
  }

  const scaleNote = (octave = 3) => pal.root * 2 ** (octave + pal.scale[Math.floor(Math.random() * pal.scale.length)] / 12);

  return {
    setFeeling,
    start() {
      bus.gain.setTargetAtTime(pal.level, T(), 2);
    },
    update(sig) {
      const thin = sig.stillnessSeconds > 4 ? Math.max(0.4, 1 - (sig.stillnessSeconds - 4) / 20) : 1;
      filter.frequency.setTargetAtTime(pal.cutoff * (0.55 + sig.intensity * 0.9) * (0.6 + 0.4 * thin), T(), 0.3);
      bus.gain.setTargetAtTime(pal.level * (0.6 + 0.5 * sig.activity) * thin, T(), 0.6);
    },
    duck(on) {
      duck.gain.setTargetAtTime(on ? 0.35 : 1, T(), 0.25);
    },
    plink(soft = 1) {
      tone({ freq: scaleNote(3 + Math.floor(Math.random() * 2)), wave: pal.plink, gain: 0.07 * soft, decay: 0.9 });
    },
    thump(solid) {
      tone({ freq: solid ? 95 : 70, endFreq: 38, gain: solid ? 0.5 : 0.22, decay: 0.45, dest: out });
      tone({ freq: 140 + Math.random() * 60, endFreq: 90, wave: 'sawtooth', gain: solid ? 0.05 : 0.02, decay: 0.25, dest: out });
    },
    swell() {
      bus.gain.cancelScheduledValues(T());
      bus.gain.setTargetAtTime(pal.level * 2.2, T(), 0.8);
      bus.gain.setTargetAtTime(pal.level, T() + 3.5, 1.5);
      filter.frequency.setTargetAtTime(pal.cutoff * 2.5, T(), 0.8);
      [0, 4, 7, 12].forEach((st, i) => setTimeout(() => tone({ freq: pal.root * 4 * 2 ** (st / 12), gain: 0.06, attack: 0.6, decay: 3.5 }), i * 180));
    },
    stop() {
      bus.gain.setTargetAtTime(0, T(), 0.5);
    },
  };
}
